import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  AllBedSensorCommandsQuery,
  BedSensorCommandNode,
  SortBy,
  SortByDirection,
} from '../../__generated__/graphql';
import MyTable, { AllColumn } from '../../components/my-table/MyTable';
import { AllFilter } from '../../components/my-table/TableFilters';
import settings from '../../settings';
import { useManyRemote } from '../../shared/use-many-remote';
import useSafeState from '../../shared/use-safe-state';
import {
  allBedSensorCommandsQuery,
  extractAllBedSensorCommands,
} from './constants';
import useBedSensorCommandUpdates from './use-bs-command-updates';

const initialOrder: SortBy = {
  key: 'timestampMs',
  order: SortByDirection.Desc,
};

const dateFormat = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'short',
  timeStyle: 'long',
});

const queryId = 'bedSensorCommandsTable';

interface Props {
  filters: any;
}

function BedSensorCommandsTable({ filters: parentFilters }: Props) {
  const filters = useRef(parentFilters || {});
  const sortBy = useRef<SortBy[]>([initialOrder]);

  const {
    loading,
    data: startRows,
    hasNextPage,
    next,
    refetch,
  } = useManyRemote<AllBedSensorCommandsQuery, BedSensorCommandNode>({
    query: allBedSensorCommandsQuery,
    extract: extractAllBedSensorCommands,
    first: settings.querySize,
    filters: filters.current,
    sortBy: sortBy.current,
    fetchPolicy: 'network-only',
    queryId,
  });

  const [rows, setRows, _rows] = useSafeState<BedSensorCommandNode[]>([]);
  useEffect(() => {
    setRows(startRows);
  }, [startRows, setRows]);

  const onUpdate = useCallback(
    (bs: BedSensorCommandNode) => {
      setRows([bs, ..._rows.current]);
    },
    [setRows, _rows],
  );

  useBedSensorCommandUpdates(parentFilters, onUpdate);

  const allFilters: AllFilter[] = useMemo(() => [], []);

  const allColumns: AllColumn[] = useMemo(
    () => [
      {
        key: 'timestampMs',
        title: 'Timestamp',
        component: (row: any) => (
          <span>{dateFormat.format(new Date(row.timestampMs))}</span>
        ),
        sortable: true,
        width: 160,
      },
      {
        key: 'message',
        title: 'Message',
        component: (row: any) => (
          <span
            style={{
              color:
                row.ok === true
                  ? 'green'
                  : row.ok === false
                    ? 'red'
                    : undefined,
            }}
          >
            {row.message}
          </span>
        ),
        sortable: false,
        width: 400,
      },
    ],
    [],
  );

  return (
    <MyTable
      initialOrder={initialOrder}
      rows={rows}
      sortBy={sortBy}
      refetch={refetch}
      filters={filters}
      allFilters={allFilters}
      allColumns={allColumns}
      hasNextPage={!!hasNextPage}
      next={next}
      loading={loading}
      hasFullText={false}
      parentFilters={parentFilters}
    />
  );
}

export default BedSensorCommandsTable;
